import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import InfoIcon from 'icons/info.svg';
import { produce } from 'immer';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { accessControlPermissionsToggleReducer } from 'reducers/accessControlPermissionsToggleReducer';
import voca from 'voca';

import useRequestError from 'components/hooks/useRequestError';
import {
  useCreateUserMutation,
  useRestoreUserMutation,
  useUpdateUserMutation,
} from 'components/hooks/userMutations';

import CheckboxField from 'components/application/CheckboxField';
import CircleQuestion from 'components/application/CircleQuestion';
import ErrorMessage from 'components/application/ErrorMessage';
import SidePanel from 'components/application/SidePanel';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { buildSelectedAttributes } from 'components/settings/users/helpers/searchImportablePersonnelField';
import useUserForm from 'components/settings/users/hooks/useUserForm';
import SearchImportablePersonnelField from 'components/settings/users/SearchImportablePersonnelField';
import TeamsAccessExplanationNotification from 'components/settings/users/TeamsAccessExplanationNotification';
import UserDetailsEditor from 'components/settings/users/UserDetailsEditor';
import UserPermissionsAndDivisionAccessInfoCard from 'components/settings/users/UserPermissionsAndDivisionAccessInfoCard';
import FormFooter from 'components/sidepanels/FormFooter';

import { identityProviderNames } from 'components/helpers/identityProviders';
import { resourceShape } from 'components/helpers/serialisableResources';
import {
  accountPermissions,
  doesUserConsumeFullUserAllowance,
  isAnyPermissionActivelySelected,
  isAnyPermissionSelected,
  isPermissionActivelySelected,
  isPermissionSelected,
  permissions,
  permissionsExcludingAdmin,
  userParamsFromState,
} from 'components/helpers/users';

const defaultCheckboxFieldProps = {
  className: 'tw-w-auto',
  labelTextProps: { className: 'tw-font-medium' },
  tooltipDelay: 500,
  tooltipType: 'hover',
};

export default function UserSidePanel({
  areProjectsEnabled,
  context,
  division,
  domainUser,
  isAtFullUserCapacity,
  isAtPersonnelReporteeManagerCapacity,
  isMultiDivisionAccount,
  isOpen,
  isSamlSsoAllowed,
  isTeamsAllowed,
  onClose,
  onCreateSuccess,
  onRestoreSuccess,
  onUpdateSuccess,
  samlProviderConfiguration,
}) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();
  const assignablePermissions = division.meta.assignablePermissions || [];

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const { pluralRAMSName, singularRAMSName } = currentActor.division.attributes;

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { currentUser, resetAccessControls, resetUserForm, setCurrentUser } =
    useUserForm({
      assignablePermissionsPerDivision: {
        [division.id]: assignablePermissions,
      },
      domainUser,
    });

  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const restoreUserMutation = useRestoreUserMutation();

  const areMutationsLoading =
    createUserMutation.isLoading ||
    updateUserMutation.isLoading ||
    restoreUserMutation.isLoading;

  const computedContext =
    currentUser.selectedRestorableUserId ? 'restore'
    : domainUser ? 'edit'
    : currentUser.personnelUserId ? 'upgradePersonnelUser'
    : 'new';

  const isEmailFieldEditable = !!currentUser.email; // add personnel logic

  const handlePersonSelection = (option) => {
    resetRequestError();

    setCurrentUser((prevState) => ({
      ...prevState,
      isSelected: true,
      ...buildSelectedAttributes(option, prevState),
    }));
  };

  const handleSelectionReset = (options = { emailOnly: false }) => {
    const resettingAttributes =
      options.emailOnly ?
        { email: '' }
      : { email: '', firstName: '', lastName: '' };

    setCurrentUser({
      ...currentUser,
      isDerived: false,
      isSelected: true,
      ...resettingAttributes,
    });
  };

  const handleSelectionEdit = () =>
    setCurrentUser({ ...currentUser, isDerived: false });

  const handleDetailsChange = (changeOptions) =>
    setCurrentUser({
      ...currentUser,
      [changeOptions.attribute]: changeOptions.value,
    });

  const handleMutationError = (e) => {
    handleRequestError(e);

    const errorTypes =
      e.response.status === 422 ?
        e.response.data.errors.flatMap(({ types }) => types)
      : [];

    const isFullUserLimitReached = errorTypes.includes('limit_reached');

    const isPersonnelReporteeManagerLimitReached = errorTypes.includes(
      'personnel_reportee_manager_limit_reached',
    );

    if (isFullUserLimitReached || isPersonnelReporteeManagerLimitReached) {
      resetAccessControls();

      queryClient.invalidateQueries({
        queryKey: ['accountUserAllowance'],
      });

      breadBoard.addInedibleToast({
        fullMessage:
          isPersonnelReporteeManagerLimitReached ?
            i18nTCommon(
              'settings.errors.pleaseUpgradeYourPlanToAddMoreTeamAndLineManagers',
            )
          : areProjectsEnabled ?
            i18nTCommon(
              'settings.errors.userlimitReachedButYouCanStillAddProjectViewers',
              {
                interpolation: { escapeValue: false },
                singularRAMSName,
              },
            )
          : i18nTCommon('settings.errors.pleaseUpgradeYourPlanToAddMoreUsers'),
      });

      resetRequestError();
    }
  };

  const handleSubmit = () => {
    const userId =
      domainUser?.data.id ||
      currentUser.selectedRestorableUserId ||
      currentUser.personnelUserId;

    const userParams = userParamsFromState({
      currentUser,
      domainUser,
    });

    if (['edit', 'upgradePersonnelUser'].includes(computedContext)) {
      updateUserMutation.mutate(
        { id: userId, userParams },
        {
          onSuccess: (data) => {
            resetUserForm();

            queryClient.invalidateQueries({
              queryKey: ['accountUserAllowance'],
            });

            queryClient.invalidateQueries({ queryKey: ['users'] });
            onUpdateSuccess(data);
          },
          onError: (e) => {
            handleMutationError(e);
          },
        },
      );
    } else if (computedContext === 'restore') {
      restoreUserMutation.mutate(
        { id: userId, userParams },
        {
          onSuccess: (data) => {
            const formattedData = produce(data, (draft) => {
              const userIndex = data.included.findIndex(
                (inclusion) =>
                  inclusion.type === 'user' &&
                  inclusion.id === data.data.relationships.user.data.id,
              );
              const [includedUser] = draft.included.splice(userIndex, 1);

              draft.data = includedUser;
            });

            resetUserForm();

            queryClient.invalidateQueries({
              queryKey: ['accountUserAllowance'],
            });

            queryClient.invalidateQueries({ queryKey: ['users'] });
            onRestoreSuccess(formattedData);
          },
          onError: (e) => {
            handleMutationError(e);
          },
        },
      );
    } else {
      createUserMutation.mutate(
        { userParams },
        {
          onSuccess: (data) => {
            resetUserForm();

            queryClient.invalidateQueries({
              queryKey: ['accountUserAllowance'],
            });

            queryClient.invalidateQueries({ queryKey: ['users'] });
            onCreateSuccess(data);
          },
          onError: (e) => {
            handleMutationError(e);
          },
        },
      );
    }
  };

  const handleClose = () => {
    resetUserForm();
    resetRequestError();
    onClose();
  };

  const handlePermissionChange = (permission) => {
    const accessControls = accessControlPermissionsToggleReducer(
      currentUser.accessControls,
      {
        type: 'TOGGLE_PERMISSION',
        payload: {
          assignablePermissionsPerDivision: {
            [division.id]: assignablePermissions,
          },
          divisionId: division.id,
          permission,
        },
      },
    );
    const accessType =
      !isMultiDivisionAccount && permission === 'administrator' ?
        (
          isPermissionActivelySelected(
            accessControls[division.id]?.administrator,
          )
        ) ?
          'administrator'
        : 'regular'
      : currentUser.accessType;

    setCurrentUser({ ...currentUser, accessControls, accessType });
  };

  const isCurrentActorSelfViewing =
    domainUser?.data.id === currentActor.user.id;

  const isSuperUser = ['primary', 'administrator'].includes(
    currentActor.user.attributes.accessType,
  );

  const accessSelection = currentUser.accessControls[division.id];

  const isAnyHigherAssignableProjectPermissionSelected =
    isAnyPermissionActivelySelected({
      accessSelection,
      permissionsToCheck: [
        'projectApprover',
        'projectEditor',
        'contentEditor',
      ].filter((permission) => assignablePermissions.includes(permission)),
    });

  const isCourseApplicableResource =
    currentActor.isAllowedFeature('training_register');

  const isPrimaryUserViewingSelf =
    currentActor.user.attributes.accessType === 'primary' &&
    isCurrentActorSelfViewing;

  const isDivisionAdministratorOrAccountAdministrator =
    isPermissionActivelySelected(accessSelection?.administrator) ||
    currentUser.accessType === 'administrator';

  const permissionCheckedState = Object.fromEntries(
    permissionsExcludingAdmin
      .concat('rolesAndCoursesEditor')
      .map((permission) => [
        permission,
        isPermissionSelected(accessSelection?.[permission]),
      ]),
  );

  const isFullUserAllowanceConsumptionDisabled =
    isAtFullUserCapacity &&
    (!domainUser ||
      !doesUserConsumeFullUserAllowance({
        accessControls: domainUser.included,
        accessType: domainUser.data.attributes.accessType,
        rolesAndCoursesEditor: domainUser.data.attributes.rolesAndCoursesEditor,
      }));

  const isAdminOrUserAllowanceConsumptionDisabled =
    isDivisionAdministratorOrAccountAdministrator ||
    isFullUserAllowanceConsumptionDisabled;

  const isPersonnelReporteeManagerAssignable = assignablePermissions.includes(
    'personnelReporteeManager',
  );

  const domainUserAccessControl = domainUser?.included.find(
    ({ relationships }) => relationships.company.data.id === division.id,
  );

  const doesDomainUserHavePersonnelReporteeManagerForDivision =
    isPersonnelReporteeManagerAssignable &&
    !!domainUserAccessControl?.attributes.personnelReporteeManager;

  const isPersonnelReporteeManagerAllowanceConsumptionDisabled =
    isAtPersonnelReporteeManagerCapacity &&
    !doesDomainUserHavePersonnelReporteeManagerForDivision;

  const isAccessSelectionRequired =
    !isPrimaryUserViewingSelf &&
    domainUser?.data.attributes.accessType !== 'administrator';

  const isSubmitDisabledForMissingAccessSelection =
    isAccessSelectionRequired &&
    !isAnyPermissionSelected({
      accessSelection,
      permissionsToCheck: [...permissions, ...accountPermissions],
    });

  return (
    <SidePanel
      bodyContent={
        <div className='popup__body-form tw-pt-0'>
          <div className='tw-mb-6 tw-mt-8'>
            {isSamlSsoAllowed &&
              samlProviderConfiguration &&
              context === 'new' && (
                <div className='tw-m-0 tw-mb-6 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
                  <div className='tw-mr-3 tw-h-5'>
                    <InfoIcon
                      className='[&_path]:tw-fill-cyan-800'
                      height={20}
                      width={20}
                    />
                  </div>
                  <p className='tw-m-0'>
                    <Trans
                      components={{
                        span: <span className='tw-font-semibold' />,
                      }}
                      i18nKey='authentication.mustBeAddedToIdentityProvider'
                      ns='common'
                      values={{
                        providerName:
                          identityProviderNames[
                            samlProviderConfiguration.attributes.providerName
                          ],
                      }}
                    />
                  </p>
                </div>
              )}
            {Object.keys(requestError.validationErrors).length > 0 && (
              <div className='tw-mb-6'>
                <ErrorMessage
                  isFallback={requestError.isFallback}
                  modifiers='side-panel__alert side-panel__alert--danger side-panel__alert--wide'
                  validationErrors={requestError.validationErrors}
                />
              </div>
            )}
            <h3 className='tw-text-l tw-font-semibold tw-tracking-auto'>
              {i18nTCommon('shared.userDetails')}
            </h3>
          </div>
          {!currentUser.email && (
            <div
              className={classNames(
                'form-group',
                requestError.validationErrors.email?.fieldHighlighted &&
                  'collection-select--invalid',
              )}
            >
              <SearchImportablePersonnelField
                inclPersonnelWithProfileAccess={context === 'new'}
                key={String(isOpen)}
                onInputChange={(val) => {
                  if (val === domainUser?.data.attributes.email) {
                    handlePersonSelection({ isNotDerived: true, value: val });
                  }
                }}
                onMenuOpen={() => {
                  removeErrorStyling({ target: { name: 'email' } });
                }}
                onSelection={handlePersonSelection}
              />
            </div>
          )}
          {currentUser.isSelected && (
            <UserDetailsEditor
              currentUser={currentUser}
              formContext={context}
              isEmailFieldEditable={isEmailFieldEditable}
              onDetailsChange={handleDetailsChange}
              onSelectionEdit={handleSelectionEdit}
              onSelectionReset={handleSelectionReset}
              removeErrorStyling={removeErrorStyling}
              requestError={requestError}
            />
          )}
          <hr className='tw-mb-6 tw-mt-8' />
          <h3 className='tw-mb-6 tw-mt-0 tw-text-l tw-font-semibold tw-tracking-auto'>
            {i18nTCommon('shared.permissions')}
          </h3>
          <div className='tw-mb-6'>
            <UserPermissionsAndDivisionAccessInfoCard />
          </div>
          {isTeamsAllowed && <TeamsAccessExplanationNotification />}
          {isPrimaryUserViewingSelf && (
            <>
              <div className='tw-mb-1 tw-text-grey-900'>
                {i18nTCommon('settings.accountOwner')}
              </div>
              <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                {i18nTCommon('settings.changeAccountOwner')}
              </div>
            </>
          )}
          {domainUser?.data.attributes.accessType === 'administrator' && (
            <>
              <div className='tw-mb-1 tw-text-grey-900'>
                {i18nT(
                  'permissions.noPermissionsReason.accountAdministrator.title',
                )}
              </div>
              <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                {i18nT(
                  'permissions.noPermissionsReason.accountAdministrator.description',
                )}
              </div>
            </>
          )}
          {!isPrimaryUserViewingSelf &&
            domainUser?.data.attributes.accessType !== 'administrator' && (
              <ul className='checkbox-collection list-unstyled'>
                <li className='checkbox-collection__item tw-mb-7 tw-flex tw-items-center tw-gap-2.5'>
                  <CheckboxField
                    {...defaultCheckboxFieldProps}
                    checked={isDivisionAdministratorOrAccountAdministrator}
                    disabled={
                      isCurrentActorSelfViewing ||
                      isFullUserAllowanceConsumptionDisabled
                    }
                    label={
                      isMultiDivisionAccount ?
                        i18nTCommon('settings.userRoles.divisionAdmin.label')
                      : i18nTCommon('settings.userRoles.accountAdmin.label')
                    }
                    name='administrator'
                    onChange={() => handlePermissionChange('administrator')}
                    tooltip={
                      isCurrentActorSelfViewing &&
                      i18nTCommon(
                        'settings.cannotDowngradeOwnAccessAndPermissions',
                      )
                    }
                  />
                  <Tooltip
                    className='tooltip-dark--max-w-xxs'
                    placement='top'
                    tooltip={
                      areProjectsEnabled ?
                        i18nTCommon(
                          isMultiDivisionAccount ?
                            'settings.userRoles.divisionAdmin.tooltip'
                          : 'settings.userRoles.accountAdmin.tooltip',
                          {
                            interpolation: { escapeValue: false },
                            pluralRAMSName: voca.capitalize(pluralRAMSName),
                          },
                        )
                      : i18nTCommon(
                          isMultiDivisionAccount ?
                            'settings.userRoles.divisionAdmin.tooltipWithoutProjects'
                          : 'settings.userRoles.accountAdmin.tooltipWithoutProjects',
                        )
                    }
                    trigger='hover'
                  >
                    <CircleQuestion size='sm' />
                  </Tooltip>
                </li>
                <hr className='tw-mb-5 tw-mt-5' />
                <h4 className='tw-mb-5 tw-text-s tw-font-semibold'>
                  {voca.capitalize(singularRAMSName)}
                </h4>
                {assignablePermissions.includes('contentEditor') && (
                  <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                    <CheckboxField
                      {...defaultCheckboxFieldProps}
                      checked={permissionCheckedState.contentEditor}
                      disabled={isAdminOrUserAllowanceConsumptionDisabled}
                      label={i18nTCommon(
                        'settings.userRoles.contentEditor.label',
                      )}
                      name='contentEditor'
                      onChange={() => handlePermissionChange('contentEditor')}
                    />
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='top'
                      tooltip={i18nTCommon(
                        'settings.userRoles.contentEditor.tooltip',
                      )}
                      trigger='hover'
                    >
                      <CircleQuestion size='sm' />
                    </Tooltip>
                  </li>
                )}
                {assignablePermissions.includes('projectApprover') && (
                  <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                    <CheckboxField
                      {...defaultCheckboxFieldProps}
                      checked={permissionCheckedState.projectApprover}
                      disabled={isAdminOrUserAllowanceConsumptionDisabled}
                      label={i18nTCommon(
                        'settings.userRoles.projectApprover.label',
                        {
                          interpolation: { escapeValue: false },
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      name='projectApprover'
                      onChange={() => handlePermissionChange('projectApprover')}
                    />
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='top'
                      tooltip={i18nTCommon(
                        'settings.userRoles.projectApprover.tooltip',
                        {
                          interpolation: { escapeValue: false },
                          pluralRAMSName: voca.capitalize(pluralRAMSName),
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      trigger='hover'
                    >
                      <CircleQuestion size='sm' />
                    </Tooltip>
                  </li>
                )}
                {assignablePermissions.includes('projectEditor') && (
                  <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                    <CheckboxField
                      {...defaultCheckboxFieldProps}
                      checked={permissionCheckedState.projectEditor}
                      disabled={isAdminOrUserAllowanceConsumptionDisabled}
                      label={i18nTCommon(
                        'settings.userRoles.projectEditor.label',
                        {
                          interpolation: { escapeValue: false },
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      name='projectEditor'
                      onChange={() => handlePermissionChange('projectEditor')}
                    />
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='top'
                      tooltip={i18nTCommon(
                        'settings.userRoles.projectEditor.tooltip',
                        {
                          interpolation: { escapeValue: false },
                          pluralRAMSName: voca.capitalize(pluralRAMSName),
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      trigger='hover'
                    >
                      <CircleQuestion size='sm' />
                    </Tooltip>
                  </li>
                )}
                {assignablePermissions.includes('projectViewer') && (
                  <li className='checkbox-collection__item tw-mb-7 tw-flex tw-items-center tw-gap-2.5'>
                    <CheckboxField
                      {...defaultCheckboxFieldProps}
                      checked={permissionCheckedState.projectViewer}
                      disabled={
                        isAnyHigherAssignableProjectPermissionSelected ||
                        isDivisionAdministratorOrAccountAdministrator
                      }
                      label={i18nTCommon(
                        'settings.userRoles.projectViewer.label',
                        {
                          interpolation: { escapeValue: false },
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      name='projectViewer'
                      onChange={() => handlePermissionChange('projectViewer')}
                      tooltip={
                        isAnyHigherAssignableProjectPermissionSelected &&
                        i18nTCommon(
                          'settings.allUsersWithHigherProjectPermissionsCanViewProjects',
                          {
                            interpolation: { escapeValue: false },
                            pluralRAMSName,
                            singularRAMSName,
                          },
                        )
                      }
                    />
                    <Tooltip
                      className='tooltip-dark--max-w-xxs'
                      placement='top'
                      tooltip={i18nTCommon(
                        'settings.userRoles.projectViewer.tooltip',
                        {
                          interpolation: { escapeValue: false },
                          pluralRAMSName: voca.capitalize(pluralRAMSName),
                          singularRAMSName: voca.capitalize(singularRAMSName),
                        },
                      )}
                      trigger='hover'
                    >
                      <CircleQuestion size='sm' />
                    </Tooltip>
                    <div className='tw-rounded-sm tw-bg-lime-050 tw-px-2 tw-py-1 tw-text-xs tw-font-bold tw-text-lime-700'>
                      {i18nTCommon('shared.free')}
                    </div>
                  </li>
                )}

                <h4 className='tw-mb-5 tw-text-s tw-font-semibold'>
                  {i18nTCommon('shared.training')}
                </h4>

                <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                  <CheckboxField
                    {...defaultCheckboxFieldProps}
                    checked={permissionCheckedState.rolesAndCoursesEditor}
                    disabled={
                      !isSuperUser || isFullUserAllowanceConsumptionDisabled
                    }
                    label={
                      isCourseApplicableResource ?
                        i18nTCommon(
                          'settings.userRoles.rolesAndCoursesEditor.label',
                        )
                      : i18nTCommon('settings.userRoles.rolesEditor.label')
                    }
                    labelProps={{
                      className:
                        isSuperUser ? '' : (
                          'hhq-checkbox__container--disabled-inactive-filled'
                        ),
                    }}
                    name='rolesAndCoursesEditor'
                    onChange={() =>
                      handlePermissionChange('rolesAndCoursesEditor')
                    }
                    tooltip={
                      !isSuperUser &&
                      i18nT(
                        `permissions.rolesAndCoursesEditor.disabledTooltip.${isCourseApplicableResource ? 'courseApplicable' : 'default'}`,
                      )
                    }
                  />
                  <Tooltip
                    className='tooltip-dark--max-w-xxs'
                    placement='top'
                    tooltip={i18nTCommon(
                      isCourseApplicableResource ?
                        'settings.userRoles.rolesAndCoursesEditor.tooltip'
                      : 'settings.userRoles.rolesEditor.tooltip',
                    )}
                    trigger='hover'
                  >
                    <CircleQuestion size='sm' />
                  </Tooltip>
                </li>
                <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                  <CheckboxField
                    {...defaultCheckboxFieldProps}
                    checked={permissionCheckedState.personnelEditor}
                    disabled={isAdminOrUserAllowanceConsumptionDisabled}
                    label={i18nTCommon(
                      'settings.userRoles.personnelEditor.label',
                    )}
                    name='personnelEditor'
                    onChange={() => handlePermissionChange('personnelEditor')}
                  />
                  <Tooltip
                    className='tooltip-dark--max-w-xxs'
                    placement='top'
                    tooltip={i18nTCommon(
                      'settings.userRoles.personnelEditor.tooltip',
                    )}
                    trigger='hover'
                  >
                    <CircleQuestion size='sm' />
                  </Tooltip>
                </li>
                <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                  <CheckboxField
                    {...defaultCheckboxFieldProps}
                    checked={permissionCheckedState.personnelViewer}
                    disabled={
                      permissionCheckedState.personnelEditor ||
                      isAdminOrUserAllowanceConsumptionDisabled
                    }
                    label={i18nTCommon(
                      'settings.userRoles.personnelViewer.label',
                    )}
                    name='personnelViewer'
                    onChange={() => handlePermissionChange('personnelViewer')}
                    tooltip={
                      permissionCheckedState.personnelEditor &&
                      i18nTCommon(
                        'settings.allUsersWithPermissionsToEditPersonnelCanViewThem',
                      )
                    }
                  />
                  <Tooltip
                    className='tooltip-dark--max-w-xxs'
                    placement='top'
                    tooltip={i18nTCommon(
                      'settings.userRoles.personnelViewer.tooltip',
                    )}
                    trigger='hover'
                  >
                    <CircleQuestion size='sm' />
                  </Tooltip>
                </li>
                {isPersonnelReporteeManagerAssignable && (
                  <li className='checkbox-collection__item tw-flex tw-items-center tw-gap-2.5'>
                    <CheckboxField
                      {...defaultCheckboxFieldProps}
                      checked={permissionCheckedState.personnelReporteeManager}
                      disabled={
                        isDivisionAdministratorOrAccountAdministrator ||
                        isPersonnelReporteeManagerAllowanceConsumptionDisabled
                      }
                      label={i18nTCommon(
                        'settings.userRoles.personnelReporteeManager.label',
                      )}
                      name='personnelReporteeManager'
                      onChange={() =>
                        handlePermissionChange('personnelReporteeManager')
                      }
                    />
                    <Tooltip
                      className='tooltip-dark--max-w-xxs tw-whitespace-pre-line'
                      placement='top'
                      tooltip={i18nTCommon(
                        'settings.userRoles.personnelReporteeManager.tooltip',
                      )}
                      trigger='hover'
                    >
                      <CircleQuestion size='sm' />
                    </Tooltip>
                  </li>
                )}
              </ul>
            )}
        </div>
      }
      closeCallback={handleClose}
      color='tertiary'
      footerContent={
        <FormFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          onSubmitTooltip={
            isSubmitDisabledForMissingAccessSelection ?
              i18nT('permissions.missingAccessSelectionTooltip')
            : undefined
          }
          submitButtonDisabled={
            submitDisabled ||
            areMutationsLoading ||
            isSubmitDisabledForMissingAccessSelection
          }
          text={
            {
              restore: i18nTCommon('shared.restoreUser'),
              new: i18nTCommon('shared.addUser'),
              edit: i18nTCommon('shared.saveChanges'),
              upgradePersonnelUser: i18nTCommon('shared.addUser'),
            }[computedContext]
          }
        />
      }
      headerContent={
        <div
          className={classNames(
            'popup__title',
            context === 'new' ?
              'popup__title--tertiary-split'
            : 'popup__title--tertiary',
          )}
        >
          {context === 'new' ?
            <Trans
              components={{
                h1: (
                  /* eslint-disable-next-line jsx-a11y/heading-has-content */
                  <h1 className='tw-mb-2 tw-text-s tw-font-medium tw-tracking-tight' />
                ),
                h2: (
                  /* eslint-disable-next-line jsx-a11y/heading-has-content */
                  <h2 className='truncated-text-container--wrapped truncated-text-container--five-lines tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900' />
                ),
              }}
              i18nKey='settings.users.UserSidePanel.new.title'
              ns='components'
              tOptions={{ interpolation: { escapeValue: false } }}
              values={{ divisionName: division.attributes.name }}
            />
          : <h2 className='truncated-text-container tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {i18nTCommon('shared.editUser')}
            </h2>
          }
        </div>
      }
      isOpen={isOpen}
    />
  );
}

UserSidePanel.propTypes = {
  context: PropTypes.string.isRequired,
  division: resourceShape('company').isRequired,
  domainUser: PropTypes.shape({
    data: resourceShape('user').isRequired,
    included: PropTypes.arrayOf(resourceShape('accessControl')),
  }),
  isMultiDivisionAccount: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool,
  isAtFullUserCapacity: PropTypes.bool,
  isAtPersonnelReporteeManagerCapacity: PropTypes.bool,
  isSamlSsoAllowed: PropTypes.bool.isRequired,
  isTeamsAllowed: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
  onRestoreSuccess: PropTypes.func.isRequired,
  onUpdateSuccess: PropTypes.func.isRequired,
  samlProviderConfiguration: PropTypes.object,
};
